import React, { useState } from 'react';
import { Card, Typography, Table, Button, Space, Tag, Input, DatePicker, message, Badge, Tabs } from 'antd';
import { MailOutlined, SendOutlined, InboxOutlined, StarOutlined, FileTextOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailsAPI } from '@services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Email {
  id: string;
  subject: string;
  from_email: string;
  to_email: string;
  body: string;
  status: string;
  type: string;
  is_starred: boolean;
  sent_at: string;
}

const Email: React.FC = () => {
  const queryClient = useQueryClient();
  const [composeVisible, setComposeVisible] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [activeTab, setActiveTab] = useState('inbox');

  // Fetch emails
  const { data: emailsData, isLoading } = useQuery({
    queryKey: ['emails'],
    queryFn: () => emailsAPI.list(),
  });

  // Compose email mutation
  const composeMutation = useMutation({
    mutationFn: (data: any) => emailsAPI.send(data),
    onSuccess: () => {
      message.success('Email sent successfully');
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      setComposeVisible(false);
    },
    onError: (error: any) => {
      message.error(`Failed to send email: ${error.message}`);
    },
  });

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => {
        const color = status === 'sent' ? 'green' : status === 'draft' ? 'orange' : 'blue';
        return <Badge color={color} status={status === 'sent' ? 'success' : 'processing'} />;
      },
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      render: (subject: string, record: Email) => (
        <Space>
          {record.is_starred && <StarOutlined style={{ color: '#faad14' }} />}
          <Text strong={!record.status || record.status === 'unread'}>{subject}</Text>
        </Space>
      ),
    },
    {
      title: 'From',
      dataIndex: 'from_email',
      key: 'from_email',
      width: 200,
    },
    {
      title: 'To',
      dataIndex: 'to_email',
      key: 'to_email',
      width: 200,
    },
    {
      title: 'Date',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 150,
      render: (date: string) => dayjs(date).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'outbound' ? 'blue' : 'green'}>
          {type === 'outbound' ? 'Sent' : 'Received'}
        </Tag>
      ),
    },
  ];

  const handleSendEmail = () => {
    const formData = new FormData();
    // In a real implementation, you'd collect form data
    composeMutation.mutate({
      subject: 'Test Email',
      to_email: 'test@example.com',
      body: 'This is a test email',
      type: 'outbound',
      status: 'sent',
    });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <Title level={2}>
          <MailOutlined /> Email
        </Title>
        <Space>
          <Button 
            type="primary" 
            icon={<SendOutlined />}
            onClick={() => setComposeVisible(true)}
          >
            Compose
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane 
          tab={
            <span>
              <InboxOutlined />
              Inbox
            </span>
          } 
          key="inbox" 
        />
        <Tabs.TabPane 
          tab={
            <span>
              <SendOutlined />
              Sent
            </span>
          } 
          key="sent" 
        />
        <Tabs.TabPane 
          tab={
            <span>
              <FileTextOutlined />
              Drafts
            </span>
          } 
          key="drafts" 
        />
        <Tabs.TabPane 
          tab={
            <span>
              <StarOutlined />
              Starred
            </span>
          } 
          key="starred" 
        />
      </Tabs>

      <Card>
        <Table
          columns={columns}
          dataSource={emailsData?.data?.data || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} emails`,
          }}
          onRow={(record: Email) => ({
            onClick: () => setSelectedEmail(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {selectedEmail && (
        <Card 
          title={selectedEmail.subject} 
          style={{ marginTop: '16px' }}
          extra={
            <Button 
              icon={<StarOutlined />} 
              type="text"
              onClick={() => {
                // Toggle star functionality
                message.info('Star toggled');
              }}
            />
          }
        >
          <div style={{ marginBottom: '16px' }}>
            <Text strong>From: </Text> {selectedEmail.from_email}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <Text strong>To: </Text> {selectedEmail.to_email}
          </div>
          <div style={{ marginBottom: '16px' }}>
            <Text strong>Date: </Text> {dayjs(selectedEmail.sent_at).format('MMM D, YYYY h:mm A')}
          </div>
          <div style={{ whiteSpace: 'pre-wrap', padding: '16px', background: '#f5f5f5', borderRadius: '4px' }}>
            {selectedEmail.body}
          </div>
        </Card>
      )}
    </div>
  );
};

export default Email;
